import create from 'zustand';
import { devtools } from 'zustand/middleware';
import { presetToValue } from '@diana/core';
import { DateTime } from 'luxon';
import { useFiltersStore } from './useFilters';
import { HubsNames, useSignalRHubStore } from './useSignalR';

type MessageState = {
  messages: Record<string, any[]>;
  stableLoadingFlags: Record<string, boolean>;
};

type MessageActions = {
  addMessage: (hubName: string, message: any) => void;
  setMessages: (hubName: string, messages: any[]) => void;
  setLoadingFlag: (hubName: string, loading: boolean) => void;
  reset: () => void;
  resetHubMessages: (hubName: string) => void;
  setStableLoadingFlag: (hubName: string, loading: boolean) => void;
  getMessagesLength: (hubNames: [string]) => number;
};

const initialState: MessageState = {
  messages: {},
  stableLoadingFlags: {},
};

const filtersStores = new Map<string, ReturnType<typeof useFiltersStore>>();

export const useMessageStore = create<MessageState & MessageActions>(
  devtools(
    (set, get) => {
      const messageBuffers: Record<string, any[]> = {};
      const messageTimeouts: Record<string, NodeJS.Timeout | null> = {};
      const batchingInProgress: Record<string, boolean> = {};
      const loadingDebounceTimers: Record<string, NodeJS.Timeout | null> = {};

      const updateStableLoadingFlag = (hubName: string, loading: boolean) => {
        if (loadingDebounceTimers[hubName]) {
          clearTimeout(loadingDebounceTimers[hubName] as NodeJS.Timeout);
        }

        if (loading) {
          set((state) => ({
            stableLoadingFlags: {
              ...state.stableLoadingFlags,
              [hubName]: true,
            },
          }), false, 'updateStableLoadingFlag');
        } else {
          loadingDebounceTimers[hubName] = setTimeout(() => {
            set((state) => ({
              stableLoadingFlags: {
                ...state.stableLoadingFlags,
                [hubName]: false,
              },
            }), false, 'updateStableLoadingFlag');
          }, 2000);
        }
      };

      const flushMessages = (hubName: string) => {
        set((state) => {
          const storeId = hubName === HubsNames.deliverySummary ? 'bunkering-logs' : hubName;
          
          const filtersStore = filtersStores.get(storeId);
          const preset = filtersStore?.getTimeRangePreset() || null;
          
          const isUpdating = useSignalRHubStore.getState().hubs[hubName].updating;
          const newMessages = messageBuffers[hubName] || [];
          const allMessages = isUpdating
            ? newMessages
            : [...(state.messages[hubName] || []), ...newMessages];

          let updatedMessages = allMessages;

          if (
            preset &&
            hubName !== HubsNames.aggregateDashboard &&
            hubName !== HubsNames.bunkeringOverview &&
            hubName !== HubsNames.aggregateOverview &&
            hubName !== HubsNames.activeSessionBunkeringDashboard
          ) {
            const [startDate, endDate] = presetToValue({ preset }) || [];

            const startTime = DateTime.fromISO(startDate);
            const endTime = DateTime.fromISO(endDate);

            updatedMessages = [];
            for (let i = 0; i < allMessages.length; i++) {
              const message = allMessages[i];
              let messageStartTime: DateTime | null = null;
              let messageEndTime: DateTime | null = null;

              if (message.bunkerActivePeriod) {
                messageStartTime = DateTime.fromISO(message.bunkerActivePeriod.start?.date);
                messageEndTime = message.bunkerActivePeriod.end
                  ? DateTime.fromISO(message.bunkerActivePeriod.end?.date)
                  : null;
              } else if (message.incidentPeriod) {
                messageStartTime = DateTime.fromISO(message.incidentPeriod.start?.date);
                messageEndTime = message.incidentPeriod.end
                  ? DateTime.fromISO(message.incidentPeriod.end?.date)
                  : null;
              } else if (message.timestamp) {
                messageStartTime = DateTime.fromISO(message.timestamp?.date);
              }

              const isWithinRange = (time: DateTime | null) => {
                return time && time >= startTime && time <= endTime;
              };

              if (message.timestamp) {
                if (messageStartTime && messageStartTime >= startTime) {
                  updatedMessages.push(message);
                }
              } else {
                if (
                  (messageStartTime && isWithinRange(messageStartTime)) ||
                  (messageEndTime && isWithinRange(messageEndTime)) ||
                  (messageStartTime && !messageEndTime && messageStartTime <= endTime)
                ) {
                  updatedMessages.push(message);
                }
              }
            }
          }

          batchingInProgress[hubName] = false;
          messageBuffers[hubName] = [];

          if (messageTimeouts[hubName]) {
            clearTimeout(messageTimeouts[hubName] as NodeJS.Timeout);
            messageTimeouts[hubName] = null;
          }
          useSignalRHubStore.getState().setUpdating(hubName, false);

          updateStableLoadingFlag(hubName, false);

          return {
            messages: {
              ...state.messages,
              [hubName]: updatedMessages,
            },
          };
        }, false, 'flushMessages');
      };

      return {
        ...initialState,
        addMessage: (hubName: string, message: any) => {
          const hubState = useSignalRHubStore.getState().hubs[hubName];
          if (hubState?.isDisposed) {
            return;
          }

          if (!messageBuffers[hubName]) {
            messageBuffers[hubName] = [];
          }
          messageBuffers[hubName].push(message);

          if (!messageTimeouts[hubName]) {
            batchingInProgress[hubName] = true;
            updateStableLoadingFlag(hubName, true);
            messageTimeouts[hubName] = setTimeout(() => {
              flushMessages(hubName);
            }, 2000);
          }
        },
        setMessages: (hubName: string, messages: any[]) => {
          set(
            (state) => {
              updateStableLoadingFlag(hubName, false);
              return {
                messages: {
                  ...state.messages,
                  [hubName]: messages,
                },
              };
            },
            false,
            'setMessages'
          );
        },
        resetHubMessages: (hubName: string) => {
          messageBuffers[hubName] = [];
          if (messageTimeouts[hubName]) {
            clearTimeout(messageTimeouts[hubName] as NodeJS.Timeout);
            messageTimeouts[hubName] = null;
          }
          if (loadingDebounceTimers[hubName]) {
            clearTimeout(loadingDebounceTimers[hubName] as NodeJS.Timeout);
            loadingDebounceTimers[hubName] = null;
          }

          set(
            (state) => ({
              messages: {
                ...state.messages,
                [hubName]: [],
              },
              stableLoadingFlags: {
                ...state.stableLoadingFlags,
                [hubName]: false,
              },
            }),
            false,
            'resetHubMessages'
          );
        },
        reset: () => {
          set(
            () => {
              Object.keys(messageBuffers).forEach((hubName) => {
                messageBuffers[hubName] = [];
                if (messageTimeouts[hubName]) {
                  clearTimeout(messageTimeouts[hubName] as NodeJS.Timeout);
                  messageTimeouts[hubName] = null;
                }
                batchingInProgress[hubName] = false;
              });

              return initialState;
            },
            false,
            'reset'
          );
        },
        setStableLoadingFlag: (hubName: string, loading: boolean) => {
          updateStableLoadingFlag(hubName, loading);
        },
        getMessagesLength: (hubNames: [string]) => {
          return hubNames.reduce((acc, hubName) => {
            return acc + (get().messages[hubName] || []).length;
          }, 0);
        },
      };
    },
    'Message Store'
  )
);

export const registerFiltersStore = (storeId: string, store: ReturnType<typeof useFiltersStore>) => {
  filtersStores.set(storeId, store);
};
